import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { type ShiftCountDurationBucket } from '@libraries/typescript/api_client';

type ShiftDurationHistogramProps = {
    readonly buckets: Array<ShiftCountDurationBucket>;
    // Fires with the clicked bucket's covered-shift-count bounds for a runs
    // drill (max is null for the open-ended top bucket).
    readonly onBucketClick?: (point: HistogramPoint) => void;
};

type HistogramPoint = {
    readonly label: string;
    readonly avgSeconds: number | null;
    readonly minMs: number | null;
    readonly avgMs: number | null;
    readonly maxMs: number | null;
    readonly runCount: number;
    readonly minShiftCount: number;
    readonly maxShiftCount: number | null;
};

function formatDurationMs(durationMs: number | null): string {
    if (durationMs === null) {
        return '—';
    }
    const seconds = durationMs / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
}

type HistogramTooltipPayload = {
    readonly payload?: HistogramPoint;
};

function ShiftDurationTooltip({
    active,
    payload,
}: {
    readonly active?: boolean;
    readonly payload?: Array<HistogramTooltipPayload>;
}) {
    if (!active || !payload?.[0]?.payload) {
        return null;
    }
    const point = payload[0].payload;
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{point.label} shifts</div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Avg</span>
                <span className="tabular-nums">
                    {formatDurationMs(point.avgMs)}
                </span>
            </div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Min</span>
                <span className="tabular-nums">
                    {formatDurationMs(point.minMs)}
                </span>
            </div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Max</span>
                <span className="tabular-nums">
                    {formatDurationMs(point.maxMs)}
                </span>
            </div>
            <div className="text-muted-foreground mt-1">
                {point.runCount} runs
            </div>
        </div>
    );
}

export function ShiftDurationHistogram({
    buckets,
    onBucketClick,
}: ShiftDurationHistogramProps) {
    const data: Array<HistogramPoint> = buckets.map((bucket) => ({
        label: bucket.label,
        avgSeconds:
            bucket.avg_duration_ms === null
                ? null
                : bucket.avg_duration_ms / 1000,
        minMs: bucket.min_duration_ms,
        avgMs: bucket.avg_duration_ms,
        maxMs: bucket.max_duration_ms,
        runCount: bucket.run_count,
        minShiftCount: bucket.min_shift_count,
        maxShiftCount: bucket.max_shift_count,
    }));
    const hasAnyRuns = data.some((point) => point.runCount > 0);
    if (data.length === 0 || !hasAnyRuns) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center rounded-md border text-sm">
                No completed runs to plot.
            </div>
        );
    }

    return (
        <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid
                        stroke="var(--border)"
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                        tick={{ fontSize: 11 }}
                        width={48}
                        tickFormatter={(value: number) =>
                            formatDurationMs(value * 1000)
                        }
                    />
                    <RechartsTooltip content={<ShiftDurationTooltip />} />
                    <Bar
                        dataKey="avgSeconds"
                        name="Average run time"
                        fill="var(--chart-1)"
                        isAnimationActive={false}
                        cursor={onBucketClick ? 'pointer' : undefined}
                        onClick={
                            onBucketClick
                                ? (entry: {
                                      readonly payload?: HistogramPoint;
                                  }) => {
                                      if (entry.payload !== undefined) {
                                          onBucketClick(entry.payload);
                                      }
                                  }
                                : undefined
                        }
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
